// Offline-aware image component that uses cached images when offline
import React, { useState, useEffect } from 'react';
import { Package } from 'lucide-react';

import { useOffline } from '../hooks/useOffline';

const OfflineImage = ({ 
  src, 
  alt = '', 
  className = '', 
  fallbackIcon: FallbackIcon = Package,
  type = 'product',
  onLoad,
  onError,
  ...props 
}) => {
  const { isOffline, getCachedImage, cacheImage } = useOffline();
  const [imageSrc, setImageSrc] = useState(src);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadImage = async () => {
      if (!src) {
        setHasError(true);
        setIsLoading(false);
        return;
      }

      // If online and not already base64, cache the image
      if (!isOffline && !src.startsWith('data:')) {
        try {
          const cachedData = await cacheImage(src, type);
          // Don't update src if we got the same URL back (caching failed)
          if (cachedData && cachedData !== src) {
            setImageSrc(cachedData);
          }
        } catch (_error) {
          console.warn('[OfflineImage] Caching failed, using original URL');
        }
      }
      
      // If offline, try to get cached version
      if (isOffline && !src.startsWith('data:')) {
        try {
          const cachedData = await getCachedImage(src);
          if (cachedData) {
            setImageSrc(cachedData);
          } else {
            // No cached version available
            setHasError(true);
          }
        } catch (_error) {
          setHasError(true);
        }
      }
      
      setIsLoading(false);
    };

    loadImage();
  }, [src, isOffline, getCachedImage, cacheImage, type]);

  const handleLoad = (e) => {
    setIsLoading(false);
    setHasError(false);
    onLoad?.(e);
  };

  const handleError = (e) => {
    setIsLoading(false);
    setHasError(true);
    onError?.(e);
  };

  if (hasError || !imageSrc) {
    return (
      <div 
        className={`flex items-center justify-center bg-gray-100 ${className}`}
        {...props}
      >
        <FallbackIcon className="w-8 h-8 text-gray-400" />
      </div>
    );
  }

  return (
    <>
      {isLoading && (
        <div 
          className={`flex items-center justify-center bg-gray-100 animate-pulse ${className}`}
          {...props}
        >
          <FallbackIcon className="w-8 h-8 text-gray-300" />
        </div>
      )}
      <img
        src={imageSrc}
        alt={alt}
        className={`w-full h-full ${isLoading ? 'hidden' : ''}`}
        style={{ objectFit: 'contain' }}
        onLoad={handleLoad}
        onError={handleError}
        {...props}
      />
    </>
  );
};

export default OfflineImage;
